import { CSSProperties, useRef, useState } from 'react';

import { createShareUrl } from '@/cutils/shareurls/createShareUrl';
import { IconSystem } from '@/icons/IconSystem';

import styles from './SocialMediaPopover.module.scss';
import { createShareText } from './SocialMediaPopover.utils';
import { IconSystemLink } from './base/IconButton/IconSystemLink';
import { Tooltip } from './base/Tooltip/Tooltip';

type Props = {
	headline?: string | null;
	url: string;
	onClose?: () => void;
	position: 'left' | 'right' | 'top' | 'bottom' | undefined;
	arrowPositionLeft: number;
	arrowPositionTop: number;
};

export function SocialMediaPopover({ headline, url, onClose, position, arrowPositionLeft, arrowPositionTop }: Props) {
	const facebookShareUrl = createShareUrl('facebook', createShareText('facebook', headline), 'Facebook', url);
	const twitterShareUrl = createShareUrl('twitter', createShareText('twitter', headline), 'Twitter', url);
	const whatsAppShareUrl = createShareUrl('whatsapp', createShareText('whatsapp', headline), 'WhatsApp', url);
	const pocketShareURL = createShareUrl('pocket', headline as string, 'Pocket', url);

	const normalShareUrl = createShareUrl('url', '', 'Link', url);

	const [copySuccess, setCopySuccess] = useState(false);

	const urlRefObject = useRef<HTMLInputElement>(null);

	/**
	 * Copies the value of the URL input field to the clipboard and sets the copySuccess state to true.
	 */
	const copyToClipboard = () => {
		const urlToCopy = urlRefObject?.current?.value;
		if (urlToCopy) {
			navigator.clipboard.writeText(urlToCopy);
			setCopySuccess(true);
		}
	};

	const positionClass = position ? styles[position] : '';

	// the popup library will move the popup into the visible area if it does not fit on the screen
	// we do account for it here
	let arrowStyle: CSSProperties | undefined;
	switch (position) {
		case 'bottom':
			arrowStyle = { left: arrowPositionLeft, top: 0 };
			break;
		case 'top':
			arrowStyle = { left: arrowPositionLeft, bottom: 0 };
			break;
		case 'right':
			arrowStyle = { top: arrowPositionTop, left: 0 };
			break;
		case 'left':
			arrowStyle = { top: arrowPositionTop, right: 0 };
			break;
		default:
			arrowStyle = undefined;
			break;
	}

	return (
		<div className={`${styles.popOverWrapper} ${positionClass ?? ''}`} data-theme-area="dark">
			<div className={`${styles.arrow}`} style={arrowStyle} />
			<div className={`${styles.popOver} body3`}>
				<div className={`${styles.popOverHeader} heading4`}>
					<span>Teilen auf:</span>
					<button className={`${styles.actionButton} button-link`} onClick={() => onClose?.()}>
						<IconSystem icon="close" variant="filled" role="button" />
					</button>
				</div>

				<div className={styles.socialMediaIcons}>
					<IconSystemLink
						type="tertiary"
						icon="twitter-x"
						variant="filled"
						role="button"
						target="_blank"
						href={twitterShareUrl}
					></IconSystemLink>

					<IconSystemLink
						type="tertiary"
						icon="facebook"
						variant="filled"
						role="button"
						target="_blank"
						href={facebookShareUrl}
					></IconSystemLink>

					<IconSystemLink
						type="tertiary"
						icon="whatsapp"
						variant="filled"
						role="button"
						target="_blank"
						href={whatsAppShareUrl}
					></IconSystemLink>

					<IconSystemLink type="tertiary" icon="pocket" variant="filled" target="_blank" href={pocketShareURL}></IconSystemLink>
				</div>
				<div className={styles.popOverFooter}>
					<span>Link kopieren:</span>
					<div className={styles.copySection}>
						<div className={styles.linkSharerWrapper}>
							<IconSystem icon="link" variant="filled" size="s" />
							<input className={styles.url} disabled={true} type="text" name="url" defaultValue={normalShareUrl} ref={urlRefObject} />
						</div>

						<button className={`${styles.actionButton} button-link`} onClick={copyToClipboard}>
							<IconSystem icon="copy" variant="filled" role="button" />
						</button>
					</div>
				</div>
				{copySuccess && <Tooltip type="success" content="Link kopiert" />}
			</div>
		</div>
	);
}
